/* ===== Joy + AI — completare automată în asistentul „Creează pagina locației” =====
 * Pasul 0: comerciantul dă linkul de Facebook / Google Maps (sau nume + oraș) → AI caută pe Google.
 * Pașii 2–4: cardul „Joy a completat” cu valorile găsite → „Accept” salvează și bifează modulele.
 * Pasul 5: oferta propusă de AI + imagine generată cu AI.
 * Pasul 6: campanie (premii instant sau tombolă) + afiș generat cu AI.
 * Nimic nu se salvează fără „Accept”; campania nu pornește singură.
 */
import { getCurrentUser } from '../services/auth.js?v=83.79';
import { getCurrentSettings, loadSettings } from '../services/settings.js?v=83.79';
import { uploadFile } from '../services/storage.js?v=83.79';
import {
  saveCampaignSettingsBackend,
  quickSetupResearchBackend,
  generateAiPosterBackend,
  getAiPosterQuotaBackend,
  startCampaignBackend
} from '../services/backend.js?v=83.79';
import { showSnackbar } from '../utils/ui.js?v=83.79';
import { freshView, isFresh } from './joy-fresh.js?v=83.79';

const DAYS = [
  ['luni', 'Luni'], ['marti', 'Marți'], ['miercuri', 'Miercuri'], ['joi', 'Joi'],
  ['vineri', 'Vineri'], ['sambata', 'Sâmbătă'], ['duminica', 'Duminică']
];

/* ---------- helpers ---------- */
function el(tag, attrs = {}, ...children) {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v == null || v === false) continue;
    if (k === 'class') n.className = v;
    else if (k === 'text') n.textContent = v;
    else if (k.startsWith('on') && typeof v === 'function') n.addEventListener(k.slice(2), v);
    else if (k === 'value') n.value = v;
    else if (k === 'checked') n.checked = !!v;
    else n.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of children.flat()) {
    if (c == null || c === false) continue;
    n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return n;
}

export function isHttpUrl(v) {
  try {
    const u = new URL(String(v || '').trim());
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch (_) {
    return false;
  }
}

function hostOf(v) {
  try { return new URL(String(v).trim()).hostname.toLowerCase().replace(/^www\./, ''); } catch (_) { return ''; }
}

export function isMapsUrl(v) {
  if (!isHttpUrl(v)) return false;
  const u = new URL(v);
  const host = hostOf(v);
  // linkurile de „indicații de drum” (/maps/dir) nu sunt pagina localului
  if (/\/maps\/dir/i.test(u.pathname) || /[?&]saddr=/i.test(u.search)) return false;
  return host === 'maps.app.goo.gl' || host === 'maps.google.com' || host === 'share.google' ||
    host === 'goo.gl' || (/^google\.(com|ro)$/.test(host) && /\/maps/i.test(u.pathname + u.search));
}

export function mapsSearchUrl(name, address, city) {
  const q = [name, address || city].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export function reviewUrlFrom(mapsUrl) {
  const m = /place_id[:=]([A-Za-z0-9_-]{10,})/.exec(String(mapsUrl || ''));
  return m ? `https://search.google.com/local/writereview?placeid=${m[1]}` : mapsUrl;
}

function isoDate(offsetDays = 0) {
  const d = new Date(Date.now() + offsetDays * 86400000);
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function roDate(iso) {
  const months = ['ianuarie', 'februarie', 'martie', 'aprilie', 'mai', 'iunie', 'iulie', 'august',
    'septembrie', 'octombrie', 'noiembrie', 'decembrie'];
  const [y, m, d] = String(iso || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${d} ${months[m - 1]}`;
}

function badge(kind) {
  const map = {
    found: ['✓ Găsit', 'jai-badge--found', 'Găsit pe Google — verifică'],
    maybe: ['? Verifică', 'jai-badge--maybe', 'Găsit, dar nesigur — verifică'],
    ai: ['✨ Propus', 'jai-badge--ai', 'Propus de AI — poți modifica'],
    missing: ['Negăsit', 'jai-badge--missing', 'Nu am găsit — completează dacă vrei'],
    you: ['Completezi tu', 'jai-badge--missing', 'Completezi tu'],
    set: ['✓ Pus', 'jai-badge--found', 'Ai completat deja']
  };
  const [t, c, title] = map[kind] || map.ai;
  return el('span', { class: `jai-badge ${c}`, text: t, title });
}

function kindFor(value, conf, manual = false) {
  if (manual) return value ? 'set' : 'you';
  if (!value) return 'missing';
  return conf === 'sigur' ? 'found' : 'maybe';
}

/** Rezultatul căutării sau gol (completare manuală, fără Joy). */
function resultOf(ctx) {
  const ai = ctx.state.ai;
  const manual = !(ai.status === 'done' && ai.result);
  return { r: ai.result || {}, manual };
}

/** După „Accept”: salvez și trec singur la pasul următor. */
function acceptedGoNext(ctx, key) {
  ctx.state.ai.accepted[key] = true;
  ctx.persist();
  if (typeof ctx.next === 'function') ctx.next();
  else ctx.rerender();
}

function field(label, input, badgeKind, hint) {
  return el('label', { class: 'jai-field' },
    el('span', { class: 'jai-label' }, label, badgeKind ? badge(badgeKind) : null),
    input,
    hint ? el('span', { class: 'jai-hint', text: hint }) : null
  );
}

function input(value, attrs = {}) {
  return el('input', { class: 'jai-input', value: value || '', ...attrs });
}

async function refreshSettings() {
  const uid = getCurrentUser()?.uid;
  if (uid) {
    try { await loadSettings(uid, true); } catch (_) { /* ignore */ }
  }
}

/* ---------- AI state (persistat în draftul Joy) ---------- */
export function emptyAiState() {
  return {
    status: 'none', // none | done | skipped
    input: { name: '', city: '', facebookUrl: '', mapsUrl: '' },
    result: null,
    accepted: {},
    campaign: null,
    posterUrl: '',
    fresh: false,
    pending: {}
  };
}

export function sanitizeAiState(raw) {
  const base = emptyAiState();
  if (!raw || typeof raw !== 'object') return base;
  try {
    const json = JSON.stringify(raw);
    if (json.length > 30000) return base;
    const v = JSON.parse(json);
    return {
      status: ['none', 'done', 'skipped'].includes(v.status) ? v.status : 'none',
      input: { ...base.input, ...(v.input || {}) },
      result: v.result && typeof v.result === 'object' ? v.result : null,
      accepted: v.accepted && typeof v.accepted === 'object' ? v.accepted : {},
      campaign: v.campaign && typeof v.campaign === 'object' ? v.campaign : null,
      posterUrl: isHttpUrl(v.posterUrl) ? v.posterUrl : '',
      campaignAsked: v.campaignAsked === true,
      loyalty: v.loyalty && typeof v.loyalty === 'object' ? v.loyalty : null,
      fresh: v.fresh === true,
      pending: v.pending && typeof v.pending === 'object' && !Array.isArray(v.pending) ? v.pending : {}
    };
  } catch (_) {
    return base;
  }
}

/* ---------- Pasul 0: căutare ---------- */
export function renderAiStart(container, ctx) {
  const ai = ctx.state.ai;
  const s = freshView(getCurrentSettings(), ctx.state.ai);

  if (ai.status === 'done' && ai.result) {
    const r = ai.result;
    const found = [];
    const missing = [];
    (r.phone ? found : missing).push('telefon');
    (r.address ? found : missing).push('adresă');
    (Object.values(r.hours || {}).some(Boolean) ? found : missing).push('program');
    (r.facebookUrl ? found : missing).push('Facebook');
    (r.instagramUrl ? found : missing).push('Instagram');
    (r.tiktokUrl ? found : missing).push('TikTok');
    if (r.googleRating) found.push(`recenzii ${r.googleRating}★`);
    container.append(el('div', { class: 'joy-assistant-panel-box jai-box' },
      el('div', { class: 'jai-title', text: `✅ Am găsit „${r.displayName}”` }),
      found.length ? el('p', { class: 'jai-p' }, el('b', { text: 'Am completat: ' }), found.join(', ')) : null,
      missing.length ? el('p', { class: 'jai-p jai-muted', text: `Negăsit: ${missing.join(', ')}. Le poți completa pe parcurs.` }) : null,
      r.notes ? el('p', { class: 'jai-p jai-muted', text: `ℹ️ ${r.notes}` }) : null,
      el('p', { class: 'jai-p', text: 'Acum trecem prin fiecare pas: verifici ce am pus și apeși „Accept”.' }),
      el('button', {
        class: 'jai-link', type: 'button',
        onclick: () => { ai.status = 'none'; ai.result = null; ai.accepted = {}; ctx.persist(); ctx.rerender(); }
      }, '↺ Caută din nou')
    ));
    return;
  }

  const fb = input(ai.input.facebookUrl, { type: 'url', inputmode: 'url', placeholder: 'https://facebook.com/numele-paginii' });
  const maps = input(ai.input.mapsUrl, { type: 'url', inputmode: 'url', placeholder: 'https://maps.app.goo.gl/…' });
  // numele și orașul din contul nou (pasul „Locație”) — din setările reale, nu din pagina „de la zero”
  const raw = getCurrentSettings() || {};
  const rawCity = String(raw.locationMapAddress || '').trim();
  const cityGuess = rawCity && rawCity.length <= 40 && !/\d|,/.test(rawCity) ? rawCity : '';
  const name = input(ai.input.name || raw.locationMapName || s.locationMapName || raw.companyName || '', { maxlength: 80, placeholder: 'ex: Green Pub' });
  const city = input(ai.input.city || cityGuess, { maxlength: 60, placeholder: 'ex: Mioveni' });
  const status = el('div', { class: 'jai-status', hidden: true });
  // butonul mare e jos (în bara asistentului); acesta rămâne ascuns și face căutarea
  const go = el('button', { class: 'jai-btn jai-btn--search', type: 'button' }, '✨ Caută și completează');
  const done = () => { ctx.state.searching = false; if (typeof ctx.nav === 'function') ctx.nav(); };

  go.addEventListener('click', async () => {
    ai.input = {
      facebookUrl: fb.value.trim(),
      mapsUrl: maps.value.trim(),
      name: name.value.trim(),
      city: city.value.trim()
    };
    if (ai.input.name.length < 2 || ai.input.city.length < 2) {
      showSnackbar('Scrie numele localului și orașul (mă ajută să-l găsesc sigur).');
      (ai.input.name.length < 2 ? name : city).focus();
      return;
    }
    go.disabled = true;
    ctx.state.searching = true;
    if (typeof ctx.nav === 'function') ctx.nav();
    // „Refă pagina” = de la zero: abia acum (când chiar începi) se golește pagina veche
    if (typeof ctx.beforeStart === 'function' && !(await ctx.beforeStart())) {
      go.disabled = false;
      done();
      return;
    }
    ctx.state.searching = true;
    if (typeof ctx.nav === 'function') ctx.nav();
    go.textContent = 'Caut pe Google…';
    status.hidden = false;
    status.textContent = '🔎 Caut localul, adresa, programul și rețelele… durează 30–50 de secunde.';
    try {
      const res = await quickSetupResearchBackend(ai.input);
      if (!res?.result) throw new Error('Răspuns gol');
      ai.result = res.result;
      ai.status = 'done';
      ai.accepted = {};
      ctx.state.searching = false;
      ctx.persist();
      ctx.rerender();
    } catch (e) {
      const msg = e?.message && !/internal/i.test(e.message) ? e.message : 'Căutarea nu a mers acum.';
      status.textContent = `⚠️ ${msg} Poți încerca din nou sau poți continua manual.`;
      go.disabled = false;
      go.textContent = '✨ Încearcă din nou';
      done();
    }
  });

  const manual = el('button', {
    class: 'jai-link jai-link--manual', type: 'button',
    onclick: () => { if (!ctx.state.searching && typeof ctx.manual === 'function') ctx.manual(); }
  }, 'Completez manual, fără căutare →');

  container.append(el('div', { class: 'joy-assistant-panel-box jai-box' },
    el('div', { class: 'jai-row' }, field('Nume local *', name), field('Oraș *', city)),
    field('Link Google Maps', maps, null, 'Opțional. În Google Maps: Distribuie → Copiază linkul.'),
    field('Link pagina de Facebook', fb, null, 'Opțional.'),
    go,
    status,
    manual
  ));
}

/* ---------- Cardul „Joy a completat” pentru pașii 2–4 ---------- */
function acceptedNote(ctx, key, text) {
  return el('div', { class: 'joy-assistant-panel-box jai-box jai-box--done' },
    el('span', { text: `✅ ${text}` }),
    el('button', {
      class: 'jai-link', type: 'button',
      onclick: () => { delete ctx.state.ai.accepted[key]; ctx.persist(); ctx.rerender(); }
    }, 'Modifică')
  );
}

async function savePatch(patch, btn, ctx) {
  // „Refă pagina”: ținem în draft; aplicația publică se schimbă abia la final
  if (ctx && isFresh(ctx.state.ai)) {
    ctx.state.ai.pending = { ...(ctx.state.ai.pending || {}), ...patch };
    ctx.persist();
    return true;
  }
  btn.disabled = true;
  const old = btn.textContent;
  btn.textContent = 'Salvez…';
  try {
    await saveCampaignSettingsBackend(patch);
    await refreshSettings();
    return true;
  } catch (e) {
    showSnackbar(e?.message || 'Nu am putut salva.');
    return false;
  } finally {
    btn.disabled = false;
    btn.textContent = old;
  }
}

function renderBrandCard(container, ctx) {
  const { r, manual } = resultOf(ctx);
  const conf = r.confidence || {};
  const s = freshView(getCurrentSettings(), ctx.state.ai);
  const sm = s.socialMedia || {};
  const name = input(r.displayName || s.locationMapName || s.companyName, { maxlength: 80 });
  const tag = input(r.tagline || s.clientTagline, { maxlength: 60, placeholder: 'ex: Cafea bună, oameni buni' });
  const fb = input(r.facebookUrl || sm.facebook?.link || (isHttpUrl(ctx.state.ai.input.facebookUrl) ? ctx.state.ai.input.facebookUrl : ''), { type: 'url', placeholder: 'https://facebook.com/…' });
  const ig = input(r.instagramUrl || sm.instagram?.link, { type: 'url', placeholder: 'https://instagram.com/…' });
  const tt = input(r.tiktokUrl || sm.tiktok?.link, { type: 'url', placeholder: 'https://tiktok.com/@…' });
  const accept = el('button', { class: 'jai-btn jai-btn--accept', type: 'button' }, '✓ Accept și continuă');

  // Logo: poză încărcată pe loc
  let logoUrl = String(s.locationLogoURL || '').trim();
  const logoFile = el('input', { type: 'file', accept: 'image/*', hidden: true });
  const logoRow = el('div', { class: 'jai-logo' });
  const drawLogo = () => {
    logoRow.replaceChildren(
      logoUrl ? el('img', { src: logoUrl, alt: 'Logo', class: 'jai-logo__img' }) : el('span', { class: 'jai-logo__ph', text: '🏷️' }),
      el('button', { class: 'jai-link', type: 'button', onclick: () => logoFile.click() }, logoUrl ? 'Schimbă logo-ul' : '+ Adaugă poză cu logo-ul')
    );
  };
  logoFile.addEventListener('change', async () => {
    const f = logoFile.files && logoFile.files[0];
    if (!f) return;
    logoRow.querySelector('.jai-link').textContent = 'Se încarcă…';
    try {
      const url = await uploadFile(getCurrentUser()?.uid, 'location-logo', f);
      if (url) logoUrl = url;
    } catch (_) {
      showSnackbar('Nu am putut încărca logo-ul. Încearcă alt fișier JPG/PNG.');
    }
    drawLogo();
  });
  drawLogo();

  accept.addEventListener('click', async () => {
    const next = { ...sm };
    const picked = [];
    const set = (p, v, host) => {
      const link = String(v || '').trim();
      const prev = next[p] || {};
      if (isHttpUrl(link) && hostOf(link).includes(host)) {
        next[p] = { enabled: true, link, qrUrl: prev.qrUrl || '' };
        picked.push(p);
      } else {
        next[p] = { enabled: false, link: '', qrUrl: prev.qrUrl || '' };
      }
    };
    set('facebook', fb.value, 'facebook');
    set('instagram', ig.value, 'instagram');
    set('tiktok', tt.value, 'tiktok');
    const patch = { socialMedia: next };
    if (name.value.trim()) patch.locationMapName = name.value.trim();
    patch.clientTagline = tag.value.trim();
    if (picked.includes('facebook')) patch.clientSocialUrl = fb.value.trim();
    if (logoUrl) { patch.locationLogoURL = logoUrl; picked.push('logo'); }
    if (await savePatch(patch, accept, ctx)) {
      ctx.addModules('step3Modules', picked);
      acceptedGoNext(ctx, 'brand');
    }
  });

  container.append(el('div', { class: 'joy-assistant-panel-box jai-box' },
    manual ? null : el('div', { class: 'jai-title', text: '✨ Joy a completat — verifică și completează ce lipsește' }),
    el('div', { class: 'jai-grid2' },
      field('Nume afișat', name, manual ? kindFor(name.value, '', true) : 'ai'),
      field('Slogan', tag, manual ? kindFor(tag.value, '', true) : 'ai')
    ),
    el('div', { class: 'jai-label' }, 'Logo', badge(logoUrl ? 'set' : manual ? 'you' : 'missing')),
    logoRow, logoFile,
    el('div', { class: 'jai-sub', text: 'Rețele sociale' }),
    field('Facebook', fb, kindFor(fb.value, conf.social, manual)),
    field('Instagram', ig, kindFor(ig.value, conf.social, manual)),
    field('TikTok', tt, kindFor(tt.value, conf.social, manual)),
    accept
  ));
}

export async function renderHoursImage(name, hours) {
  const W = 1080;
  const H = 1350;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d');
  const grad = g.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, '#1f1a3a');
  grad.addColorStop(1, '#3b2a6b');
  g.fillStyle = grad;
  g.fillRect(0, 0, W, H);
  g.fillStyle = 'rgba(255,255,255,0.06)';
  g.beginPath(); g.arc(W - 120, 140, 260, 0, Math.PI * 2); g.fill();
  const font = (w, px) => `${w} ${px}px system-ui, -apple-system, Segoe UI, Roboto, sans-serif`;
  g.fillStyle = '#ffffff';
  g.textAlign = 'center';
  g.font = font(800, 92);
  g.fillText('Program', W / 2, 220);
  g.font = font(500, 44);
  g.fillStyle = 'rgba(255,255,255,0.75)';
  g.fillText(String(name || '').slice(0, 34), W / 2, 295);
  DAYS.forEach(([k, label], i) => {
    const y = 400 + i * 118;
    g.fillStyle = i % 2 === 0 ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)';
    g.fillRect(90, y - 72, W - 180, 100);
    g.textAlign = 'left';
    g.fillStyle = '#ffffff';
    g.font = font(700, 50);
    g.fillText(label, 130, y);
    g.textAlign = 'right';
    const v = String(hours[k] || '—').slice(0, 24);
    g.fillStyle = /închis|inchis/i.test(v) ? '#ffb4b4' : '#ffe08a';
    g.font = font(600, 50);
    g.fillText(v, W - 130, y);
  });
  const blob = await new Promise((res) => c.toBlob(res, 'image/jpeg', 0.9));
  if (!blob) throw new Error('Nu am putut crea imaginea cu programul');
  return new File([blob], 'program.jpg', { type: 'image/jpeg' });
}

const SHORT_DAYS = ['Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm', 'Dum'];

/** „Lun–Vin 10:00–23:00 · Sâm–Dum 12:00–24:00” */
function summarizeHours(h) {
  const vals = DAYS.map(([k]) => String(h[k] || '').trim());
  if (!vals.some(Boolean)) return '';
  const groups = [];
  vals.forEach((v, i) => {
    const last = groups[groups.length - 1];
    if (last && last.v === v) last.end = i;
    else groups.push({ v, start: i, end: i });
  });
  return groups
    .filter((g) => g.v)
    .map((g) => `${SHORT_DAYS[g.start]}${g.end > g.start ? `–${SHORT_DAYS[g.end]}` : ''} ${g.v}`)
    .join(' · ');
}

function renderUtilsCard(container, ctx) {
  const ai = ctx.state.ai;
  const { r, manual } = resultOf(ctx);
  const conf = r.confidence || {};
  const s = freshView(getCurrentSettings(), ctx.state.ai);
  const phone = input(r.phone || s.reservationPhone, { type: 'tel', maxlength: 30, placeholder: 'ex: 0722 123 456' });
  const addr = input(r.address || s.locationMapAddress, { maxlength: 200, placeholder: 'Strada, număr, oraș' });
  const mapsUrl = isMapsUrl(r.googleMapsUrl) ? r.googleMapsUrl
    : isMapsUrl(ai.input.mapsUrl) ? ai.input.mapsUrl
      : isMapsUrl(s.locationMapUrl) ? s.locationMapUrl
        : (r.displayName || r.address ? mapsSearchUrl(r.displayName, r.address, ai.input.city) : '');
  const maps = input(mapsUrl, { type: 'url', placeholder: 'https://maps.app.goo.gl/…' });
  const hours = {};
  const grid = el('div', { class: 'jai-hours', hidden: true });
  for (const [k, label] of DAYS) {
    const inp = input((r.hours && r.hours[k]) || '', { maxlength: 40, placeholder: '08:00–20:00 / Închis' });
    hours[k] = inp;
    grid.append(el('span', { text: label }), inp);
  }
  const summary = el('div', { class: 'jai-hours-sum' });
  const drawSummary = () => {
    const txt = summarizeHours(Object.fromEntries(Object.entries(hours).map(([k, i]) => [k, i.value.trim()])));
    summary.replaceChildren(
      el('span', { text: txt || 'Program negăsit' }),
      el('button', { class: 'jai-link', type: 'button', onclick: () => { grid.hidden = !grid.hidden; drawSummary(); } }, grid.hidden ? (txt ? 'Modifică' : 'Completează') : 'Gata')
    );
  };
  grid.addEventListener('input', drawSummary);
  drawSummary();
  const ssid = input(s.wifiSsid, { maxlength: 60, placeholder: 'Numele rețelei (opțional)' });
  const pass = input(s.wifiPassword, { maxlength: 60, placeholder: 'Parola' });
  const accept = el('button', { class: 'jai-btn jai-btn--accept', type: 'button' }, '✓ Accept și continuă');

  accept.addEventListener('click', async () => {
    const patch = {};
    const mods = [];
    if (phone.value.trim()) {
      patch.reservationPhone = phone.value.trim();
      patch.reservationEnabled = true;
      mods.push('reservation');
    }
    if (addr.value.trim() || maps.value.trim()) {
      if (addr.value.trim()) patch.locationMapAddress = addr.value.trim();
      patch.locationMapUrl = isMapsUrl(maps.value) ? maps.value.trim() : mapsSearchUrl(r.displayName || s.locationMapName || s.companyName, addr.value, ai.input.city);
      patch.locationMapEnabled = true;
      mods.push('location');
    }
    const hv = Object.fromEntries(Object.entries(hours).map(([k, i]) => [k, i.value.trim()]));
    if (Object.values(hv).some(Boolean)) {
      accept.disabled = true;
      accept.textContent = 'Creez imaginea cu programul…';
      try {
        const file = await renderHoursImage(r.displayName || s.locationMapName || s.companyName, hv);
        const url = await uploadFile(getCurrentUser()?.uid, 'joy-assistant', file);
        if (url) {
          patch.locationHoursImageURL = url;
          patch.locationHoursEnabled = true;
          mods.push('hours');
        }
      } catch (_) {
        showSnackbar('Nu am putut crea imaginea cu programul. O poți adăuga manual.');
      }
      accept.disabled = false;
      accept.textContent = '✓ Accept și continuă';
    }
    if (ssid.value.trim()) {
      patch.wifiSsid = ssid.value.trim();
      patch.wifiPassword = pass.value.trim();
      patch.wifiEnabled = true;
      mods.push('wifi');
    }
    if (!Object.keys(patch).length) {
      acceptedGoNext(ctx, 'utils');
      return;
    }
    if (await savePatch(patch, accept, ctx)) {
      ctx.addModules('step2Modules', mods);
      acceptedGoNext(ctx, 'utils');
    }
  });

  container.append(el('div', { class: 'joy-assistant-panel-box jai-box' },
    manual ? null : el('div', { class: 'jai-title', text: '✨ Joy a completat — verifică și completează ce lipsește' }),
    el('div', { class: 'jai-grid2' },
      field('Telefon (rezervări, comenzi)', phone, kindFor(phone.value, conf.phone, manual)),
      field('Adresă', addr, kindFor(addr.value, conf.address, manual))
    ),
    field('Link Google Maps', maps, maps.value ? (manual ? 'set' : 'found') : (manual ? 'you' : 'missing')),
    el('div', { class: 'jai-label' }, 'Program', badge(kindFor(Object.values(r.hours || {}).some(Boolean), conf.hours, manual))),
    summary,
    grid,
    el('div', { class: 'jai-label' }, 'Wi-Fi', badge(ssid.value ? 'set' : 'you')),
    el('div', { class: 'jai-grid2' }, ssid, pass),
    accept
  ));
}

function renderDuoCard(container, ctx) {
  const ai = ctx.state.ai;
  const { r, manual } = resultOf(ctx);
  const s = freshView(getCurrentSettings(), ctx.state.ai);
  const mapsUrl = isMapsUrl(r.googleMapsUrl) ? r.googleMapsUrl
    : isMapsUrl(ai.input.mapsUrl) ? ai.input.mapsUrl
      : (r.displayName ? mapsSearchUrl(r.displayName, r.address, ai.input.city) : '');
  const review = input(s.clientFeedbackUrl || (mapsUrl ? reviewUrlFrom(mapsUrl) : ''), { type: 'url', placeholder: 'Linkul de recenzii Google' });
  const menu = input(r.menuUrl || s.menuUrl, { type: 'url', placeholder: 'Link spre meniul online (opțional)' });
  let menuImages = Array.isArray(s.menuImages) ? s.menuImages.filter(isHttpUrl).slice(0, 15) : [];
  // Meniul e ori link, ori poze — în aplicație apare doar unul
  let menuMode = menuImages.length ? 'photos' : (isHttpUrl(menu.value) ? 'link' : 'photos');
  const accept = el('button', { class: 'jai-btn jai-btn--accept', type: 'button' }, '✓ Accept și continuă');

  const menuFile = el('input', { type: 'file', accept: 'image/*', multiple: true, hidden: true });
  const menuThumbs = el('div', { class: 'jai-thumbs' });
  let drawMenu = () => {
    menuThumbs.replaceChildren(
      ...menuImages.map((u, idx) => el('span', { class: 'jai-thumb' },
        el('img', { src: u, alt: '' }),
        el('button', { type: 'button', class: 'jai-thumb__x', 'aria-label': 'Șterge poza', onclick: () => { menuImages.splice(idx, 1); drawMenu(); } }, '×'))),
      el('button', { type: 'button', class: 'jai-thumb jai-thumb--add', onclick: () => menuFile.click() }, '+ Poze')
    );
  };
  menuFile.addEventListener('change', async () => {
    const files = [...(menuFile.files || [])].slice(0, 15 - menuImages.length);
    for (const f of files) {
      try {
        const url = await uploadFile(getCurrentUser()?.uid, 'location-menu', f);
        if (url) menuImages.push(url);
        drawMenu();
      } catch (_) {
        showSnackbar('Nu am putut încărca o poză. Încearcă JPG/PNG.');
      }
    }
    menuFile.value = '';
  });
  drawMenu();

  const menuBadgeSlot = el('span');
  const linkPane = el('div', { class: 'jai-pane' }, menu);
  const photoPane = el('div', { class: 'jai-pane' }, menuThumbs, menuFile);
  const segBtns = {};
  const drawMode = () => {
    linkPane.hidden = menuMode !== 'link';
    photoPane.hidden = menuMode !== 'photos';
    Object.entries(segBtns).forEach(([k, b]) => b.classList.toggle('is-on', k === menuMode));
    const has = menuMode === 'link' ? isHttpUrl(menu.value) : menuImages.length > 0;
    menuBadgeSlot.replaceChildren(badge(has ? (manual ? 'set' : 'maybe') : (manual ? 'you' : 'missing')));
  };
  const seg = el('div', { class: 'jai-seg', role: 'tablist' },
    ...[['photos', '📷 Poze'], ['link', '🔗 Link']].map(([k, label]) => {
      const b = el('button', { type: 'button', class: 'jai-seg__b', role: 'tab', onclick: () => { menuMode = k; drawMode(); } }, label);
      segBtns[k] = b;
      return b;
    })
  );
  menu.addEventListener('input', drawMode);
  const menuBlock = el('div', { class: 'jai-field jai-field--block' },
    el('span', { class: 'jai-label' }, 'Meniu / Listă prețuri', menuBadgeSlot),
    el('span', { class: 'jai-hint', text: 'Poți pune un link sau poze cu meniul, lista de prețuri, servicii etc. Pe pagină apare ce alegi aici.' }),
    seg, photoPane, linkPane
  );
  const redrawThumbs = drawMenu;
  drawMenu = () => { redrawThumbs(); drawMode(); };
  drawMode();

  accept.addEventListener('click', async () => {
    const patch = {};
    const mods = [];
    if (isHttpUrl(review.value)) {
      patch.clientFeedbackUrl = review.value.trim();
      patch.feedbackEnabled = true;
      if (r.googleRating) patch.googleRating = r.googleRating;
      if (r.googleReviewCount) patch.googleReviewCount = r.googleReviewCount;
      mods.push('googleReview');
    }
    if (menuMode === 'link' && isHttpUrl(menu.value)) {
      patch.menuUrl = menu.value.trim();
      patch.menuImages = [];
      patch.menuEnabled = true;
      mods.push('menu');
    } else if (menuMode === 'photos' && menuImages.length) {
      patch.menuUrl = '';
      patch.menuImages = menuImages;
      patch.menuEnabled = true;
      mods.push('menu');
    }
    if (!Object.keys(patch).length || await savePatch(patch, accept, ctx)) {
      ctx.addModules('step3Modules', mods);
      acceptedGoNext(ctx, 'duo');
    }
  });

  container.append(el('div', { class: 'joy-assistant-panel-box jai-box' },
    manual ? null : el('div', { class: 'jai-title', text: '✨ Joy a completat — verifică și completează ce lipsește' }),
    field('Recenzie Google', review, review.value ? (manual ? 'set' : 'found') : (manual ? 'you' : 'missing'),
      r.googleRating ? `Acum: ${r.googleRating}★ din ${r.googleReviewCount || '?'} recenzii.` : 'Butonul „Recenzie” duce clientul direct la recenzii.'),
    menuBlock,
    accept
  ));
}

export function renderAiStepCard(container, stepKey, ctx) {
  const ai = ctx.state.ai;
  if (ai.accepted[stepKey]) {
    const labels = { brand: 'Numele, logo-ul și rețelele sunt gata', utils: 'Contactul, programul și Wi-Fi-ul sunt gata', duo: 'Meniul și recenzia sunt gata' };
    container.append(acceptedNote(ctx, stepKey, labels[stepKey]));
    return true;
  }
  if (stepKey === 'brand') renderBrandCard(container, ctx);
  else if (stepKey === 'utils') renderUtilsCard(container, ctx);
  else if (stepKey === 'duo') renderDuoCard(container, ctx);
  return true;
}

/* ---------- Imagine cu AI (ofertă / afiș) ---------- */
let quotaCache = null;

function quotaText(rem) {
  if (!rem) return '';
  if (rem.unlimited) return 'Demo intern · fără limită';
  return `Mai ai ${rem.day} imagini azi · ${rem.month} luna aceasta`;
}

async function loadQuota() {
  try {
    const r = await getAiPosterQuotaBackend();
    quotaCache = r?.remaining || null;
  } catch (_) { /* ignore */ }
  return quotaCache;
}

let posterTurn = 0;
export async function generatePoster({ kind, title, lines, businessType, mode, customPrompt }) {
  // Automat = fără AI: prima poză de fundal, blurată, cu textul mare peste (gratis, instant, fără greșeli).
  // AI-ul rămâne doar la „✨ AI creativ”.
  if (mode !== 'custom') {
    const { makeLocalPoster, POSTER_LAYOUTS } = await import('./joy-poster.js?v=83.79');
    const layout = POSTER_LAYOUTS[posterTurn++ % POSTER_LAYOUTS.length];
    return makeLocalPoster(String(title || '').trim(), { lines: Array.isArray(lines) ? lines : [], kind: kind === 'campaign' ? 'campaign' : 'offer', layout });
  }
  const s = getCurrentSettings() || {};
  const bg = Array.isArray(s.bgURLs) && s.bgURLs.length ? s.bgURLs[0] : (s.bgURL || '');
  const res = await generateAiPosterBackend({
    kind,
    businessName: s.locationMapName || s.companyName || '',
    businessType: businessType || '',
    title,
    lines,
    mode: mode === 'custom' ? 'custom' : 'auto',
    customPrompt: mode === 'custom' ? customPrompt : '',
    referenceImageUrl: bg
  });
  if (!res?.url) throw new Error('Nu am primit imaginea.');
  if (res.remaining) quotaCache = res.remaining;
  return res.url;
}

/**
 * Generator reutilizabil: „⚡ Automat” sau „✍️ Descrie tu”, cu limită afișată.
 * getAuto() → { title, lines } ; onDone(url)
 */
function renderPosterGenerator(container, { kind, label, getAuto, onDone, businessType, hasImage, title, thumb = '', onRemove }) {
  let mode = 'auto';
  let thumbUrl = thumb || '';
  const quota = el('span', { class: 'jai-quota', text: quotaText(quotaCache), hidden: true });
  const status = el('div', { class: 'jai-status', hidden: true });
  const text = el('textarea', {
    class: 'jai-input jai-textarea', rows: 2, maxlength: 300,
    placeholder: 'Ce să apară: text, ofertă, produse, stil (ex: 20% reducere după ora 17, negru cu auriu)'
  });
  const customBox = el('div', { class: 'jai-custom', hidden: true }, text);
  const btnAuto = el('button', { type: 'button', class: 'is-on' }, '⚡ Automat');
  const btnCustom = el('button', { type: 'button' }, '✨ AI creativ');
  const btnOwn = el('button', { type: 'button' }, '📷 Poza ta');
  // „Poza ta”: comerciantul are deja afișul / poza — o încarcă și nu mai generăm nimic
  const file = el('input', { type: 'file', accept: 'image/*', hidden: true });
  const uploadBtn = el('button', { class: 'jai-btn jai-btn--soft', type: 'button', hidden: true }, hasImage ? '📷 Altă poză' : '📷 Alege poza');
  const go = el('button', { class: 'jai-btn jai-btn--soft', type: 'button' }, hasImage ? '🎨 Altă variantă' : `🎨 ${label}`);

  // miniatura imaginii curente, lângă buton (tap = o vezi mare)
  const thumbImg = el('img', { alt: '' });
  const thumbBox = el('a', { class: 'jai-thumb', target: '_blank', rel: 'noopener', hidden: !thumbUrl }, thumbImg);
  const removeBtn = typeof onRemove === 'function'
    ? el('button', { class: 'jai-thumb__x', type: 'button', 'aria-label': 'Șterge imaginea', onclick: (e) => { e.preventDefault(); onRemove(); } }, '×')
    : null;
  if (removeBtn) thumbBox.append(removeBtn);
  const setThumb = (u) => {
    thumbUrl = u || '';
    thumbBox.hidden = !thumbUrl;
    if (thumbUrl) { thumbImg.src = thumbUrl; thumbBox.href = thumbUrl; }
  };
  setThumb(thumbUrl);

  uploadBtn.addEventListener('click', () => file.click());
  file.addEventListener('change', async () => {
    const f = file.files?.[0];
    file.value = '';
    if (!f) return;
    const uid = getCurrentUser()?.uid;
    if (!uid) { showSnackbar('Nu ești autentificat.'); return; }
    if (f.size > 10 * 1024 * 1024) { showSnackbar('Imaginea e prea mare (max 10 MB).'); return; }
    uploadBtn.disabled = true;
    status.hidden = false;
    status.textContent = '⏫ Încarc poza…';
    try {
      const url = await uploadFile(uid, 'joy-uploads', f);
      if (!url) throw new Error('Nu am putut încărca poza.');
      status.hidden = true;
      uploadBtn.textContent = '📷 Altă poză';
      setThumb(url);
      onDone(url);
    } catch (e) {
      status.textContent = `⚠️ ${e?.message || 'Nu am putut încărca poza. Încearcă alt fișier JPG/PNG.'}`;
    } finally {
      uploadBtn.disabled = false;
    }
  });
  const setMode = (m) => {
    mode = m;
    btnAuto.classList.toggle('is-on', m === 'auto');
    btnCustom.classList.toggle('is-on', m === 'custom');
    btnOwn.classList.toggle('is-on', m === 'own');
    customBox.hidden = m !== 'custom';
    go.hidden = m === 'own';
    uploadBtn.hidden = m !== 'own';
    quota.hidden = m !== 'custom'; // limita e doar pentru AI
  };
  btnAuto.addEventListener('click', () => setMode('auto'));
  btnCustom.addEventListener('click', () => setMode('custom'));
  btnOwn.addEventListener('click', () => setMode('own'));

  go.addEventListener('click', async () => {
    const auto = getAuto();
    if (!auto) return;
    if (mode === 'custom' && text.value.trim().length < 3) {
      showSnackbar('Descrie în câteva cuvinte ce imagine vrei.');
      text.focus();
      return;
    }
    go.disabled = true;
    status.hidden = false;
    status.textContent = mode === 'custom' ? '🔎 Creez imaginea cu AI… 10–30 sec.' : '🎨 Fac poza din fundalul tău…';
    try {
      const url = await generatePoster({ kind, businessType, title: auto.title, lines: auto.lines, mode, customPrompt: text.value.trim() });
      status.textContent = '✅ Gata! Verifică textul de pe imagine.';
      go.textContent = '🎨 Altă variantă';
      setThumb(url);
      onDone(url);
    } catch (e) {
      status.textContent = `⚠️ ${e?.message || 'Nu a mers.'}`;
    } finally {
      go.disabled = false;
      quota.textContent = quotaText(quotaCache);
    }
  });

  container.append(el('div', { class: 'jai-gen' },
    el('div', { class: 'jai-gen__head' }, el('span', { class: 'jai-label', text: title || 'Imagine' }), quota),
    el('div', { class: 'jai-seg jai-seg--sm jai-seg--3' }, btnAuto, btnCustom, btnOwn),
    customBox,
    el('div', { class: 'jai-gen__row' }, thumbBox, go, uploadBtn, file),
    status
  ));
  if (!quotaCache) loadQuota().then(() => { quota.textContent = quotaText(quotaCache); });
}

/** Pasul 5: pre-completează oferta + generator de imagine. */
export function renderOfferAiTools(container, ctx, { onImage }) {
  const ai = ctx.state.ai;
  const sec = ctx.state.editableSection;
  if (ai.status === 'done' && ai.result?.offer && !ai.accepted.offerPrefill && !sec.title && !sec.text) {
    sec.title = ai.result.offer.title || '';
    sec.text = ai.result.offer.text || '';
    ai.accepted.offerPrefill = true;
    ctx.persist();
  }
  const wrap = el('div', { class: 'jai-inline' });
  container.append(wrap);
  renderPosterGenerator(wrap, {
    kind: 'offer',
    label: 'Generează imaginea',
    title: 'Imagine',
    thumb: sec.imageUrl || '',
    onRemove: () => onImage(''),
    businessType: ai.result?.businessType,
    hasImage: !!sec.imageUrl,
    getAuto: () => {
      const title = String(sec.title || '').trim();
      if (!title) { showSnackbar('Scrie întâi un titlu pentru ofertă.'); return null; }
      return { title, lines: [String(sec.text || '').trim().slice(0, 80)].filter(Boolean) };
    },
    onDone: onImage
  });
}

/* ---------- Pasul 6: campanie + afiș ---------- */
function defaultCampaign(ai) {
  const c = ai.result?.campaign || {};
  return {
    type: 'instant',
    buttonText: c.buttonText || 'Încearcă-ți norocul!',
    prizes: (c.prizes && c.prizes.length ? c.prizes : [
      { message: '10% reducere la următoarea vizită', count: 30 },
      { message: 'Un produs gratuit la alegere', count: 10 }
    ]).map((p) => ({ message: p.message, count: p.count })),
    nonWinnerMessage: c.nonWinnerMessage || 'De data asta nu ai câștigat, dar mulțumim că ne-ai vizitat!',
    claimMessage: c.claimMessage || 'Arată acest ecran la casă ca să-ți ridici premiul.',
    totalTries: c.totalTries || 300,
    drawDate: isoDate(30),
    winners: 3,
    drawPrize: 'Premiu surpriză',
    entries: 500
  };
}

export function renderCampaignBuilder(container, ctx) {
  const ai = ctx.state.ai;
  const s = getCurrentSettings() || {};
  if (!ai.campaign) ai.campaign = defaultCampaign(ai);
  const c = ai.campaign;

  if (ai.accepted.campaign) {
    const isActive = s.campaignStatus === 'active';
    const box = el('div', { class: 'joy-assistant-panel-box jai-box' });
    const status = el('div', { class: 'jai-status', hidden: true });
    if (isActive) {
      box.append(el('div', { class: 'jai-title', text: '🟢 Campania e activă!' }),
        el('p', { class: 'jai-hint', text: 'Clienții pot juca de acum. O poți opri oricând din Admin.' }));
    } else {
      const yes = el('button', { class: 'jai-btn', type: 'button' }, '▶ Da, activează acum');
      const later = el('button', { class: 'jai-btn jai-btn--soft', type: 'button' }, 'Mai târziu');
      yes.addEventListener('click', async () => {
        yes.disabled = true;
        later.disabled = true;
        status.hidden = false;
        status.textContent = 'Campania la noroc cere regulament autentificat la notar — confirmă în fereastra care se deschide.';
        try {
          // pornirea trece prin fereastra cu confirmarea notarului (OG 99/2000, art. 43)
          const { openStartCampaignModal } = await import('../services/admin.js?v=83.79');
          openStartCampaignModal();
          ai.campaignAsked = true;
          ctx.persist();
          yes.disabled = false;
          later.disabled = false;
        } catch (e) {
          status.textContent = `⚠️ ${e?.message || 'Nu am putut porni campania.'}`;
          yes.disabled = false;
          later.disabled = false;
        }
      });
      later.addEventListener('click', () => { ai.campaignAsked = true; ctx.persist(); ctx.rerender(); });
      box.append(
        el('div', { class: 'jai-title', text: ai.campaignAsked ? '✅ Campania e pregătită' : '🎯 Activezi campania acum?' }),
        el('p', { class: 'jai-hint', text: ai.campaignAsked
          ? 'O poți porni oricând din Admin cu „Activează campania”.'
          : 'Dacă activezi, clienții pot juca imediat după ce publici pagina.' }),
        ai.campaignAsked ? yes : el('div', { class: 'jai-grid2' }, later, yes),
        status
      );
      if (ai.campaignAsked) yes.textContent = '▶ Activează acum';
    }
    if (ai.posterUrl) {
      box.append(
        el('img', { class: 'jai-poster', src: ai.posterUrl, alt: 'Afișul campaniei' }),
        el('a', { class: 'jai-link', href: ai.posterUrl, target: '_blank', rel: 'noopener', download: 'afis-campanie.png' }, '⬇ Descarcă afișul pentru Facebook')
      );
    }
    if (!isActive) {
      box.append(el('button', { class: 'jai-link', type: 'button', onclick: () => { delete ai.accepted.campaign; ai.campaignAsked = false; ctx.persist(); ctx.rerender(); } }, 'Modifică campania'));
    }
    container.append(box);
    return;
  }

  if (s.campaignStatus === 'active') {
    container.append(el('div', { class: 'joy-assistant-panel-box jai-box', text: 'Ai deja o campanie activă. O modifici din Admin după ce o oprești.' }));
    return;
  }

  const box = el('div', { class: 'joy-assistant-panel-box jai-box' });
  const typeRow = el('div', { class: 'jai-seg' },
    el('button', { type: 'button', class: c.type === 'instant' ? 'is-on' : '', onclick: () => { c.type = 'instant'; ctx.persist(); ctx.rerender(); } }, '🎁 Premii instant'),
    el('button', { type: 'button', class: c.type === 'tombola' ? 'is-on' : '', onclick: () => { c.type = 'tombola'; ctx.persist(); ctx.rerender(); } }, '🎟️ Tombolă')
  );
  box.append(el('div', { class: 'jai-title', text: '✨ Joy îți propune o campanie' }), typeRow);

  const bind = (inp, key, num = false) => {
    inp.addEventListener('input', () => { c[key] = num ? (parseInt(inp.value, 10) || 0) : inp.value; ctx.persist(); });
    return inp;
  };

  if (c.type === 'instant') {
    const list = el('div', {});
    const draw = () => {
      list.replaceChildren(...c.prizes.map((p, i) => {
        const m = input(p.message, { maxlength: 120, 'aria-label': `Premiul ${i + 1}` });
        const n = input(String(p.count), { type: 'number', min: 1, max: 500, 'aria-label': 'Bucăți' });
        m.addEventListener('input', () => { p.message = m.value; ctx.persist(); });
        n.addEventListener('input', () => { p.count = parseInt(n.value, 10) || 1; ctx.persist(); });
        return el('div', { class: 'jai-prize' }, m, n,
          el('button', { class: 'jai-x', type: 'button', 'aria-label': 'Șterge', onclick: () => { c.prizes.splice(i, 1); draw(); ctx.persist(); } }, '✕'));
      }));
      if (c.prizes.length < 4) {
        list.append(el('button', { class: 'jai-link', type: 'button', onclick: () => { c.prizes.push({ message: '', count: 10 }); draw(); } }, '+ Premiu'));
      }
    };
    draw();
    box.append(
      el('div', { class: 'jai-label', text: 'Premii (text · bucăți)' }),
      list,
      el('div', { class: 'jai-row' },
        field('Text pe buton', bind(input(c.buttonText, { maxlength: 40 }), 'buttonText')),
        field('Jocuri în 30 zile', bind(input(String(c.totalTries), { type: 'number', min: 10, max: 5000, title: 'Cu cât e mai mare, cu atât premiile ies mai rar.' }), 'totalTries', true))
      ),
      field('Mesaj pentru cine nu câștigă', bind(input(c.nonWinnerMessage, { maxlength: 160 }), 'nonWinnerMessage'))
    );
  } else {
    box.append(
      el('div', { class: 'jai-explain' },
        el('b', { text: 'Cum funcționează tombola' }),
        el('ol', {},
          el('li', { text: 'Fiecare client care joacă primește un bilet și își lasă numele și telefonul, cu acord GDPR.' }),
          el('li', { text: `Pe ${roDate(c.drawDate) || 'data aleasă'} intri în Admin → Arhivă → „Extrage câștigătorii”.` }),
          el('li', { text: 'Aplicația alege aleatoriu și transparent (max. 3 câștigători).' }),
          el('li', { text: 'Tu îi suni pe câștigători și le dai premiul.' })
        )
      ),
      el('div', { class: 'jai-row' },
        field('Data extragerii', bind(input(c.drawDate, { type: 'date', min: isoDate(1) }), 'drawDate')),
        field('Câștigători', bind(input(String(c.winners), { type: 'number', min: 1, max: 3 }), 'winners', true))
      ),
      field('Ce se câștigă', bind(input(c.drawPrize, { maxlength: 80, placeholder: 'ex: Cină pentru 2 persoane' }), 'drawPrize')),
      field('Număr maxim de bilete', bind(input(String(c.entries), { type: 'number', min: 10, max: 5000 }), 'entries', true))
    );
  }

  box.append(field('Cum își ridică premiul / ce primește', bind(input(c.claimMessage, { maxlength: 200 }), 'claimMessage')));

  // Afiș AI
  renderPosterGenerator(box, {
    kind: 'campaign',
    label: 'Generează afișul',
    title: 'Afiș pentru pagină și Facebook',
    thumb: ai.posterUrl || '',
    businessType: ai.result?.businessType,
    hasImage: !!ai.posterUrl,
    getAuto: () => (c.type === 'tombola'
      ? { title: 'Tombolă cu premii', lines: [`Extragere pe ${roDate(c.drawDate)}`, `${c.winners} câștigători · ${c.drawPrize}`] }
      : { title: c.buttonText || 'Câștigă premii', lines: [c.prizes.slice(0, 2).map((p) => p.message).join(' · ')] }),
    onDone: (url) => {
      ai.posterUrl = url;
      ctx.persist();
    }
  });

  const accept = el('button', { class: 'jai-btn', type: 'button' }, '✓ Accept campania');
  accept.addEventListener('click', async () => {
    const patch = { startDate: isoDate(0) };
    if (c.type === 'tombola') {
      const entries = Math.max(10, Math.min(5000, parseInt(c.entries, 10) || 500));
      patch.prizes = [{ message: `Bilet la extragerea din ${roDate(c.drawDate)}: ${c.drawPrize}`.slice(0, 200), count: entries, image: ai.posterUrl || null }];
      patch.totalTries = entries;
      patch.endDate = c.drawDate || isoDate(30);
      patch.buttonText = 'Ia-ți biletul!';
      patch.defaultPrizeMsg = 'Mulțumim! Mai încearcă.';
    } else {
      const prizes = c.prizes
        .map((p) => ({ message: String(p.message || '').trim(), count: Math.max(1, Math.min(500, parseInt(p.count, 10) || 1)), image: null }))
        .filter((p) => p.message);
      if (!prizes.length) { showSnackbar('Adaugă cel puțin un premiu.'); return; }
      const sum = prizes.reduce((a, b) => a + b.count, 0);
      patch.prizes = prizes;
      patch.totalTries = Math.max(sum, Math.min(5000, parseInt(c.totalTries, 10) || sum * 5));
      patch.endDate = isoDate(30);
      patch.buttonText = String(c.buttonText || 'Încearcă-ți norocul!').trim().slice(0, 40);
      patch.defaultPrizeMsg = String(c.nonWinnerMessage || '').trim();
    }
    patch.claimMessage = String(c.claimMessage || '').trim();
    patch.campaignKind = c.type === 'tombola' ? 'tombola' : 'instant';
    if (await savePatch(patch, accept)) {
      ai.accepted.campaign = true;
      ctx.persist();
      ctx.rerender();
    }
  });
  box.append(accept);
  container.append(box);
}


/* ---------- Pasul 6: fidelizare (card de ștampile) ---------- */
export function renderLoyaltyBuilder(container, ctx) {
  const ai = ctx.state.ai;
  const s = getCurrentSettings() || {};
  const cur = s.loyaltySettings || {};
  if (!ai.loyalty) {
    ai.loyalty = {
      required: Math.max(1, parseInt(cur.requiredPurchases, 10) || 6),
      reward: String(cur.rewardText || '').trim() || 'o cafea gratuită',
      minAmount: Number(cur.minAmount) || 0,
      imageUrl: String(cur.promoImageUrl || '').trim()
    };
  }
  const L = ai.loyalty;
  if (s.campaignStatus === 'active') {
    container.append(el('div', { class: 'joy-assistant-panel-box jai-box', text: 'Ai o campanie activă. Fidelizarea și campania nu pot merge în același timp — oprește campania din Admin, apoi pornește fidelizarea.' }));
    return;
  }
  const box = el('div', { class: 'joy-assistant-panel-box jai-box' });
  const chips = el('div', { class: 'jai-seg jai-seg--sm' },
    [5, 6, 8, 10].map((n) => el('button', {
      type: 'button', class: L.required === n ? 'is-on' : '',
      onclick: () => { L.required = n; ctx.persist(); ctx.rerender(); }
    }, `${n} bonuri`))
  );
  const reward = input(L.reward, { maxlength: 120, placeholder: 'ex: o cafea gratuită' });
  reward.addEventListener('input', () => { L.reward = reward.value; ctx.persist(); });
  const minAmount = input(L.minAmount ? String(L.minAmount) : '', { type: 'number', min: 0, max: 10000, placeholder: 'fără minim' });
  minAmount.addEventListener('input', () => { L.minAmount = Math.max(0, Number(minAmount.value) || 0); ctx.persist(); });

  box.append(
    el('div', { class: 'jai-title', text: '⭐ Card de fidelitate' }),
    el('div', { class: 'jai-explain' },
      el('b', { text: 'Cum funcționează' }),
      el('ol', {},
        el('li', { text: 'Clientul își pune telefonul o singură dată și bifează acordul.' }),
        el('li', { text: 'După fiecare cumpărătură scanează bonul fiscal — AI-ul verifică CUI-ul, data și suma.' }),
        el('li', { text: `La ${L.required} bonuri primește recompensa. Tu o dai din Admin → Clienți.` })
      )
    ),
    el('div', { class: 'jai-label', text: 'Câte bonuri până la recompensă' }),
    chips,
    field('Ce primește clientul', reward),
    field('Suma minimă pe bon (lei, opțional)', minAmount)
  );
  renderPosterGenerator(box, {
    kind: 'offer',
    label: 'Generează poza',
    title: 'Poza cardului (opțional)',
    thumb: L.imageUrl || '',
    businessType: ai.result?.businessType,
    hasImage: !!L.imageUrl,
    getAuto: () => ({ title: 'Card de fidelitate', lines: [`La ${L.required} bonuri primești ${String(L.reward || '').trim() || 'o recompensă'}`] }),
    onDone: (url) => {
      L.imageUrl = url;
      ctx.persist();
    }
  });
  box.append(el('p', { class: 'jai-hint', text: 'Se pornește când apeși „Salvează” la final.' }));
  container.append(box);
}
